import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useGamePhase } from '@/hooks/useGamePhase'
import { StudentDecision } from '@/redux/slices/studentDecisionWebsocketSlice'
import { ResearchOptionsStatus } from '@/redux/slices/researchModeSlice'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { Checkbox } from '../ui/Checkbox'
import { ModalMode } from './types'

const EXPECTED_RESEARCH_OPTIONS_COUNT = 3

interface SendDecisionModalProps {
  isOpen: boolean
  onClose: () => void
  onSend: (
    message: string,
    isLate: boolean,
    isPublic: boolean,
    research?: { index: number; justification: string }
  ) => void
  adjudication: string
  finalDecision: string
  currentUserId?: number
  prefillDecision?: string
  mode?: ModalMode
  pastDecisions?: StudentDecision[]
  researchMode?: boolean
  researchOptions?: string[]
  researchStatus?: string
  researchError?: string | null
  onRetryResearchOptions?: () => void
  phaseId?: number | null
}

export function SendDecisionModal({
  isOpen,
  onClose,
  onSend,
  finalDecision,
  adjudication,
  currentUserId,
  prefillDecision,
  mode = ModalMode.SUBMIT,
  pastDecisions = [],
  researchMode = false,
  researchOptions = [],
  researchStatus = '',
  researchError = null,
  onRetryResearchOptions,
}: SendDecisionModalProps) {
  const [decision, setDecision] = useState('')
  const [selectedResearchIndex, setSelectedResearchIndex] = useState<
    number | null
  >(null)
  const [researchJustification, setResearchJustification] = useState<string>('')
  const [isPublic, setIsPublic] = useState(false)
  const [selectedRoundIndex, setSelectedRoundIndex] = useState<number>(0)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const { roundStatus } = useGamePhase()
  const isLateSubmission = roundStatus === ActivityState.Completed

  useEffect(() => {
    if (isOpen && (mode === ModalMode.SUBMIT || mode === ModalMode.VIEW)) {
      setDecision(finalDecision || prefillDecision || '')
    }
  }, [isOpen, finalDecision, prefillDecision, mode])

  const handleClose = useCallback(() => {
    if (mode === ModalMode.SUBMIT) {
      setDecision('')
      setIsPublic(false)
      setSelectedResearchIndex(null)
      setResearchJustification('')
      setSubmitError(null)
    }
    onClose()
  }, [mode, onClose])

  const handleSend = useCallback(() => {
    if (researchMode && mode === ModalMode.SUBMIT) {
      const hasValidOptions =
        researchStatus === ResearchOptionsStatus.READY &&
        researchOptions.length === EXPECTED_RESEARCH_OPTIONS_COUNT
      if (!hasValidOptions) {
        setSubmitError(
          'Decision options are not available yet. Please wait or retry.'
        )
        return
      }
      if (
        selectedResearchIndex === null ||
        researchOptions[selectedResearchIndex] === undefined
      ) {
        setSubmitError('Please select one action before submitting.')
        return
      }
      if (researchJustification.trim().length < 10) {
        setSubmitError('Please provide a justification of at least 10 characters.')
        return
      }
      onSend(researchOptions[selectedResearchIndex], isLateSubmission, isPublic, {
        index: selectedResearchIndex,
        justification: researchJustification,
      })
      setSubmitError(null)
    } else {
      onSend(decision, isLateSubmission, isPublic)
    }
    handleClose()
  }, [
    onSend,
    decision,
    isLateSubmission,
    isPublic,
    handleClose,
    researchMode,
    mode,
    selectedResearchIndex,
    researchOptions,
    researchJustification,
  ])

  const isFormValid = useMemo(() => {
    if (researchMode && mode === ModalMode.SUBMIT) {
      return (
        researchStatus === ResearchOptionsStatus.READY &&
        researchOptions.length === EXPECTED_RESEARCH_OPTIONS_COUNT &&
        selectedResearchIndex !== null &&
        researchJustification.trim().length >= 10
      )
    }
    return decision.trim() !== ''
  }, [
    researchMode,
    mode,
    researchStatus,
    researchOptions,
    selectedResearchIndex,
    researchJustification,
    decision,
  ])
  const hasValidResearchOptions =
    researchStatus === ResearchOptionsStatus.READY &&
    researchOptions.length === EXPECTED_RESEARCH_OPTIONS_COUNT
  const shouldShowResearchError =
    researchStatus === ResearchOptionsStatus.FAILED ||
    (researchStatus === ResearchOptionsStatus.READY && !hasValidResearchOptions)
  const researchErrorMessage =
    researchStatus === ResearchOptionsStatus.READY && !hasValidResearchOptions
      ? 'Received an unexpected decision options response. Please retry.'
      : researchError ||
        'Could not generate decision options. Please try again later.'
  const isReadOnly = useMemo(
    () => mode === ModalMode.VIEW || !!adjudication || !!finalDecision,
    [mode, adjudication, finalDecision]
  )

  const getModalTitle = useMemo(() => {
    switch (mode) {
      case 'past-decisions':
        return 'Past Decisions'
      case 'view':
        return finalDecision ? 'View Decision' : 'Decision'
      default:
        return 'Decision'
    }
  }, [mode, finalDecision])

  const getModalDescription = useMemo(() => {
    switch (mode) {
      case 'past-decisions':
        return 'Review your previous decisions and adjudications'
      case 'view':
        return adjudication
          ? 'View decision and adjudication'
          : 'View your submitted decision'
      default:
        return 'Submit final decision'
    }
  }, [mode, adjudication])

  const getDecisionOwnerLabel = useCallback(
    (decisionItem?: StudentDecision) => {
      if (!decisionItem) return 'Unknown student'
      return (
        decisionItem.user_name ||
        decisionItem.user ||
        `Student ${decisionItem.student_id}`
      )
    },
    []
  )

  const isOwnDecision = useCallback(
    (decisionItem?: StudentDecision) =>
      Boolean(currentUserId && decisionItem?.student_id === currentUserId),
    [currentUserId]
  )

  const groupedPastDecisions = useMemo(() => {
    const grouped = new Map<number, StudentDecision[]>()

    pastDecisions.forEach((decisionItem) => {
      if (decisionItem.round_number == null) return
      const round = decisionItem.round_number
      const existing = grouped.get(round) || []
      existing.push(decisionItem)
      grouped.set(round, existing)
    })

    return Array.from(grouped.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([roundNumber, decisionsForRound]) => ({
        roundNumber,
        decisions: decisionsForRound.sort((a, b) => {
          const ownA = isOwnDecision(a) ? 1 : 0
          const ownB = isOwnDecision(b) ? 1 : 0
          if (ownA !== ownB) return ownB - ownA
          return getDecisionOwnerLabel(a).localeCompare(getDecisionOwnerLabel(b))
        }),
      }))
  }, [pastDecisions, getDecisionOwnerLabel, isOwnDecision])

  const currentRoundGroup = useMemo(() => {
    return groupedPastDecisions[selectedRoundIndex]
  }, [groupedPastDecisions, selectedRoundIndex])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`${mode === ModalMode.PAST_DECISIONS ? 'sm:max-w-[35rem]' : 'sm:max-w-[27.3rem]'} max-h-[50vh] sm:max-h-[70vh] flex flex-col`}
      >
        <DialogHeader>
          <div className='flex gap-2 items-center'>
            <div className='p-3 rounded-lg bg-secondary'>
              <img src='/icons/adjudicate.svg' alt='' className='w-5 h-5' />
            </div>
            <div>
              <DialogTitle className='text-base text-[#0F172A] font-bold'>
                {getModalTitle}
              </DialogTitle>
              <DialogDescription className='text-sm text-[#64748B]'>
                {getModalDescription}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-6 overflow-y-auto flex-1 pr-1'>
          {mode === ModalMode.PAST_DECISIONS && pastDecisions.length > 0 && (
            <div className='space-y-3'>
              <Label className='text-sm font-medium text-black'>
                Select Round
              </Label>
              <div className='flex flex-wrap gap-2'>
                {groupedPastDecisions.map((roundGroup, index) => (
                  <Button
                    key={`round-${roundGroup.roundNumber}`}
                    variant={selectedRoundIndex === index ? 'default' : 'outline'}
                    size='sm'
                    onClick={() => setSelectedRoundIndex(index)}
                    className={`text-xs ${
                      selectedRoundIndex === index ? 'bg-dark-blue' : ''
                    }`}
                  >
                    Round {roundGroup.roundNumber}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {mode === ModalMode.SUBMIT && isLateSubmission && !finalDecision && (
            <Alert className='bg-amber-50 border-amber-300'>
              <ExclamationTriangleIcon className='w-5 h-5 text-amber-500' />
              <AlertTitle className='text-amber-800'>
                Late Submission
              </AlertTitle>
              <AlertDescription className='text-amber-700'>
                The time for this phase has ended. Your submission will be
                marked as late.
              </AlertDescription>
            </Alert>
          )}

          {mode !== ModalMode.PAST_DECISIONS &&
            !(researchMode && mode === ModalMode.SUBMIT) && (
              <div className='space-y-2'>
                <Label className='text-sm font-medium text-black'>
                  Your Decision
                </Label>
                <Textarea
                  readOnly={isReadOnly}
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                  placeholder='Type your decision here'
                  className='min-h-[100px] resize-none bg-[#FFFFFF] placeholder:text-[#94A3B8] placeholder:text-sm'
                />
              </div>
            )}

          {researchMode && mode === ModalMode.SUBMIT && !finalDecision && (
            <div className='space-y-4'>
              {shouldShowResearchError && (
                <Alert className='bg-red-50 border-red-200'>
                  <AlertTitle className='text-red-800'>Options unavailable</AlertTitle>
                  <AlertDescription className='text-red-700'>
                    {researchErrorMessage}
                  </AlertDescription>
                </Alert>
              )}
              {researchStatus === ResearchOptionsStatus.PENDING && (
                <div className='flex items-center gap-2 text-sm text-gray-600'>
                  <span className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600' />
                  <p>Generating decision options...</p>
                </div>
              )}
              {hasValidResearchOptions && (
                <>
                  <Label className='text-sm font-medium text-black'>
                    Choose one action
                  </Label>
                  <div className='space-y-2'>
                    {researchOptions.map((opt, idx) => (
                      <label
                        key={idx}
                        className='flex cursor-pointer gap-2 rounded-md border border-gray-200 p-3 hover:bg-gray-50'
                      >
                        <input
                          type='radio'
                          name='research-opt'
                          checked={selectedResearchIndex === idx}
                          onChange={() => setSelectedResearchIndex(idx)}
                          className='mt-1'
                        />
                        <span className='text-sm'>{opt}</span>
                      </label>
                    ))}
                  </div>
                  <div className='space-y-2'>
                    <Label className='text-sm font-medium text-black'>
                      Justification (minimum 10 characters)
                    </Label>
                    <Textarea
                      value={researchJustification}
                      onChange={(e) => setResearchJustification(e.target.value)}
                      placeholder='Explain why you chose this action'
                      className='min-h-[100px] resize-none'
                    />
                  </div>
                </>
              )}
              {shouldShowResearchError && onRetryResearchOptions && (
                <div className='flex justify-end'>
                  <Button type='button' variant='outline' onClick={onRetryResearchOptions}>
                    Retry generating options
                  </Button>
                </div>
              )}
            </div>
          )}

          {submitError && (
            <p className='text-sm text-red-600' role='alert'>
              {submitError}
            </p>
          )}

          {mode === ModalMode.SUBMIT && !adjudication && !finalDecision && (
            <div className='flex items-center space-x-2'>
              <Checkbox
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className='w-4 h-4'
              />
              <Label className='pt-1 text-sm font-medium leading-none text-black cursor-pointer'>
                Do you want to make your decision public?
              </Label>
            </div>
          )}

          {mode === ModalMode.PAST_DECISIONS && pastDecisions.length > 0 && (
            <div className='space-y-4'>
              {currentRoundGroup?.decisions.map((decisionItem) => (
                <div key={decisionItem.decision_id} className='space-y-2'>
                  <div className='p-3 bg-gray-50 rounded-md border'>
                    <div className='mb-2'>
                      <span className='inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-1 text-sm font-bold text-slate-700'>
                        {isOwnDecision(decisionItem)
                          ? 'Me'
                          : getDecisionOwnerLabel(decisionItem)}
                      </span>
                    </div>
                    <p className='text-sm whitespace-pre-wrap'>
                      {decisionItem.decision || 'No decision submitted'}
                    </p>
                  </div>
                  {decisionItem.late_submission && (
                    <div className='text-xs font-medium text-amber-600'>
                      This decision was submitted late
                    </div>
                  )}
                  {decisionItem.adjudication && (
                    <div className='space-y-2'>
                      <Label className='text-sm font-medium text-black'>
                        Adjudication
                      </Label>
                      <div className='p-3 bg-blue-50 rounded-md border border-blue-200'>
                        <p className='text-sm whitespace-pre-wrap'>
                          {decisionItem.adjudication}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {(!currentRoundGroup || currentRoundGroup.decisions.length === 0) && (
                <div className='py-6 text-center text-gray-500'>
                  <p>No decisions found for this round.</p>
                </div>
              )}
            </div>
          )}

          {mode === ModalMode.PAST_DECISIONS && pastDecisions.length === 0 && (
            <div className='py-8 text-center text-gray-500'>
              <p>No past decisions found.</p>
            </div>
          )}

          {mode !== ModalMode.PAST_DECISIONS && adjudication && (
            <div className='space-y-2'>
              <Label className='text-sm font-medium text-black'>
                Adjudication
              </Label>
              <div className='p-3 bg-blue-50 rounded-md border border-blue-200'>
                <p className='text-sm whitespace-pre-wrap'>{adjudication}</p>
              </div>
            </div>
          )}

          {mode === ModalMode.SUBMIT && !adjudication && (
            <div className='flex gap-3 justify-end'>
              <Button
                onClick={handleSend}
                disabled={
                  !isFormValid ||
                  !!finalDecision ||
                  roundStatus === ActivityState.Paused
                }
                className={`${
                  isLateSubmission
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-dark-blue hover:bg-dark-blue-hovered'
                } disabled:bg-[#CED2D8] text-white disabled:text-[#6D6E71]`}
              >
                {isLateSubmission ? 'Submit Late' : 'Send'}
              </Button>
            </div>
          )}

          {(mode === ModalMode.VIEW || mode === ModalMode.PAST_DECISIONS) && (
            <div className='flex justify-end'>
              <Button variant='outline' onClick={handleClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
